import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core'
import { Field } from '@models/ui'
import { SelectObjectOptions, SelectOption } from '@core/models'
import { generateUuid } from '@core/global-util'
import { SelectSettingsFormService } from '@app/feature/add-field/add-field-content/select-settings/select-settings-form.service'
import { cloneDeep, map } from 'lodash-es'
import { moveItemInArray } from '@nomad2102npm/cdk/drag-drop'
import { SelectOptionComponent } from '@app/feature/add-field/add-field-content/select-settings/select-option/select-option.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { TbDragDropEvent } from '@components-library/tb-drag-and-drop/drag-drop.types'
import { TbDragDirective } from '@components-library/tb-drag-and-drop/tb-drag.directive'
import { TbDropContainerDirective } from '@components-library/tb-drag-and-drop/tb-drop-container.directive'
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco'
import { take } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { shareReplay } from 'rxjs'

@UntilDestroy()
@Component({
    selector: 'app-dropdown-select-cell-settings',
    imports: [
        TbDragDirective,
        TbDropContainerDirective,
        SelectOptionComponent,
        TbButtonComponent,
        TranslocoDirective,
    ],
    providers: [SelectSettingsFormService],
    templateUrl: './dropdown-select-cell-settings.component.html',
})
export class DropdownSelectCellSettingsComponent implements OnInit, OnChanges {
    private readonly selectSettingsFormService = inject(SelectSettingsFormService)
    private readonly translation = inject(TranslocoService)

    @Input() field!: Field

    selectOptions!: SelectObjectOptions

    @Output() fieldChanged: EventEmitter<Field> = new EventEmitter<Field>()
    @Output() valid = new EventEmitter<boolean>()

    defaultOption?: SelectOption
    dropdownOptions: SelectOption[] = []

    baseNames$ = this.translation
        .selectTranslateObject('status_configuration')
        .pipe(take(1), untilDestroyed(this), shareReplay(1))

    ngOnInit() {
        this.selectOptions = cloneDeep(this.field.select_object_field)!

        if (this.selectOptions) {
            this.dropdownOptions = this.convertOptionsToArray()
        }

        if (!this.selectOptions) {
            this.preFillForEmptyDropdown()
        }

        this.setDefaultOnInit()
        this.selectSettingsFormService.setSelectGroupControl(this.selectOptions, this.field)
        this.selectSettingsFormService.getStatusChanges().subscribe((status) => {
            this.valid.emit(status === 'VALID')
        })

        this.selectSettingsFormService.getValueChanges().subscribe((value) => {
            this.updateOptions(value)
        })
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.field) {
            this.setDefaultOnInit()
        }
    }

    getOptionGroup(guid: string) {
        return this.selectSettingsFormService.getOptionGroup(guid)
    }

    add(color = '#cccccc', label = '', addControl = true) {
        const newGuid = `new-${generateUuid()}`
        const newOption: SelectOption = {
            label,
            color,
            icon: '',
        }

        if (addControl) {
            this.selectSettingsFormService.addGroupControl(newOption, newGuid, this.field)
        }

        this.dropdownOptions.push({ guid: newGuid, ...newOption })
        this.selectOptions = {
            ...this.selectOptions,
            [newGuid]: newOption,
        }
        this.valid.emit(false)
    }

    delete(guid: string) {
        this.selectSettingsFormService.deleteGroupControl(guid)

        if (this.field.select_object_field_sorted) {
            const index = this.dropdownOptions.findIndex((option) => {
                return option.guid === guid
            })
            this.dropdownOptions.splice(index, 1)
            this.emitSortedData()
            return
        }

        delete this.selectOptions[guid]
        this.emitData()
    }

    drop(event: TbDragDropEvent | Event) {
        const dragDropEvent = event as TbDragDropEvent
        if (dragDropEvent.previousIndex === null || dragDropEvent.currentIndex === null) return

        moveItemInArray(
            this.dropdownOptions,
            dragDropEvent.previousIndex,
            dragDropEvent.currentIndex,
        )
        this.emitSortedData()
    }

    getDropdownDragData(option: SelectOption, index: number) {
        return { optionGuid: option.guid, index }
    }

    updateOptions(options: SelectOption[]) {
        options.forEach((option) => {
            this.updateOption(option)
        })

        this.fieldChanged.emit({
            ...this.field,
            select_object_field: this.selectOptions,
        })
    }

    optionChange(option: SelectOption) {
        this.updateOption(option)

        this.fieldChanged.emit({
            ...this.field,
            select_object_field: this.selectOptions,
        })
    }

    private updateOption(option: SelectOption) {
        const guid = option.guid!
        this.selectOptions[guid] = { ...option }
        delete this.selectOptions[guid].guid
    }

    private emitSortedData() {
        this.fieldChanged.emit({
            ...this.field,
            select_object_field_sorted: this.dropdownOptions,
        })
    }

    private emitData() {
        this.dropdownOptions = this.convertOptionsToArray()
        this.fieldChanged.emit({
            ...this.field,
            select_object_field: this.selectOptions,
        })
    }

    private convertOptionsToArray(): SelectOption[] {
        return map(this.selectOptions, (option: SelectOption, guid) => {
            return {
                guid,
                ...option,
            }
        })
    }

    private setDefaultOnInit() {
        const defaultGuid = this.field.default_value

        if (!defaultGuid) {
            if (this.defaultOption) delete this.defaultOption

            return
        }

        this.defaultOption = {
            guid: defaultGuid,
            ...this.selectOptions[defaultGuid],
        }
    }

    private preFillForEmptyDropdown() {
        this.baseNames$.subscribe((names) => {
            this.add('#C5E1A5', names['new'], false)
            this.add('#FFF9C4', names['test'], false)

            this.fieldChanged.emit({
                ...this.field,
                select_object_field: this.selectOptions,
            })
            this.valid.emit(true)
        })
    }
}
